import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Add these imports
import { ArrowLeft, Search } from "lucide-react";
import { useCreatives } from "../../../../context/CreativeContext";
import { Creative } from "../../../../types/creative.types";
import ItemListPopup from "../../../ui/ItemListPopup";
import ToastContainer, { useToast } from "../../../ui/Toast_Message";
import CustomButton from "../../../ui/CommonButton";
import CreativeSelectionCard from "../../../ui/CreativeSelectionCard";

import { ReactComponent as All } from "../../../../assets/icons/svg/all.svg";
import { ReactComponent as Landscape } from "../../../../assets/icons/svg/landscape.svg";
import { ReactComponent as Protrait } from "../../../../assets/icons/svg/Protrait.svg";

const ReplaceCreativeScreen: React.FC = () => {
  const { creativeId } = useParams<{ creativeId: string }>(); // Get creativeId from URL
  const navigate = useNavigate(); // For navigation
  const { creatives, updateCreative } = useCreatives();
  const [selectedCreative, setSelectedCreative] = useState<Creative | null>(
    null
  );
  const [showPreview, setShowPreview] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "landscape" | "portrait">("all");
  const { showToast, toasts } = useToast();

  // Find the current creative
  const currentCreative = creatives.find(
    (creative) => creative.id === creativeId
  );

  // If creative not found, redirect back
  if (!currentCreative) {
    navigate("/creatives/list");
    return null;
  }

  const availableCreatives = creatives.filter(
    (creative) => creative.id !== currentCreative.id
  );

  const filteredCreatives = availableCreatives.filter((creative) => {
    const matchesSearch = creative.title
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesFilter = filter === "all" || creative.orientation === filter;
    return matchesSearch && matchesFilter;
  });

  const handleCreativeSelect = (creative: Creative) => {
    setSelectedCreative(creative);
    setShowPreview(true);
  };

  const handleReplaceConfirm = () => {
    if (selectedCreative) {
      // Update the current creative with the new creative's data
      updateCreative(currentCreative.id, {
        imageUrl: selectedCreative.imageUrl,
        orientation: selectedCreative.orientation,
        // Add any other properties you want to replace
      });
      showToast("success", "", "Replaced successfully!");
      setShowPreview(false);
      // Navigate back to the list
      navigate("/creatives/list");
    }
  };

  const handlePreviewClose = () => {
    setShowPreview(false);
    setSelectedCreative(null);
  };

  const handleClose = () => {
    navigate("/creatives/list");
  };

  return (
    <div className="min-h-screen p-6 bg-white">
      {" "}
      {/* Remove fixed positioning and z-index */}
      <div className="max-w-[1920px] mx-auto">
        {/* Header with back button */}
        {/* <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleClose}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-xl font-semibold text-gray-900">
            Replace Creative: {currentCreative.title}
          </h1>
        </div> */}

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-sm 2xl:text-base 4xl:text-lg font-medium text-gray-900">
            Available {availableCreatives.length} creatives
          </h2>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search content"
                className="w-64 pl-10 pr-4 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-sm"
              />
            </div>

            <div className="flex gap-1 ml-1 text-gray-600">
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "all"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("all")}
              >
                <All className="w-5 h-5" />
              </div>
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "landscape"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("landscape")}
              >
                <Landscape className="w-5 h-5" />
              </div>
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "portrait"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("portrait")}
              >
                <Protrait className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {filteredCreatives.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="text-gray-400 text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No other creatives available
              </h3>
              <p className="text-gray-500">
                Upload more creatives to replace this one
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-4 3xl:grid-cols-4 4xl:grid-cols-5 gap-6">
            {filteredCreatives.map((creative) => (
              <CreativeSelectionCard
                key={creative.id}
                creative={creative}
                isSelected={selectedCreative?.id === creative.id}
                onSelect={() => handleCreativeSelect(creative)}
              />
            ))}
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} />
      {showPreview && selectedCreative && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <ItemListPopup title="Preview" onClose={handlePreviewClose}>
            <div className="flex flex-col h-full">
              <div className="flex-1 mb-6">
                <div className="mb-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-[#323232]">Dimension</p>
                    <p className="text-sm font-medium text-[#A3A4A9]">
                      {selectedCreative.orientation === "landscape"
                        ? "1920 x 1080"
                        : "1080 x 1920"}
                    </p>
                  </div>
                </div>

                <div className="w-full bg-gradient-to-br from-orange-100 to-yellow-100 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                  <img
                    src={selectedCreative.imageUrl}
                    alt={selectedCreative.title}
                    className="max-w-full max-h-full object-contain rounded"
                    style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))" }}
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end gap-3">
                  <CustomButton variant="outline" onClick={handlePreviewClose}>
                    Cancel
                  </CustomButton>
                  <CustomButton
                    isActive={true}
                    onClick={handleReplaceConfirm}
                    className="bg-[#0091AF] hover:bg-[#007B8A] text-white"
                  >
                    Apply changes
                  </CustomButton>
                </div>
              </div>
            </div>
          </ItemListPopup>
        </div>
      )}
    </div>
  );
};

export default ReplaceCreativeScreen;
